<template>
    <app-title />
    <!-- eslint-disable max-len -->
    <div ref="el"
         class="bg-black h-screen w-screen overflow-hidden">
        <nav class="fixed inset-x-0 top-0 h-12 bg-black/50 flex items-center z-40 px-4 transition-transform"
             :class="{'-translate-y-full': !isNavVisible}">
            <div class="flex items-center space-x-2 mr-auto">
                <HeadlessMenu v-if="render.languages.length"
                              as="div"
                              class="relative">
                    <div>
                        <MenuButton class="reader-link flex items-center">
                            <chat-bubble-bottom-center-text-icon class="h-8 w-8" />
                        </MenuButton>
                    </div>
                    <transition enter-active-class="transition ease-out duration-100"
                                enter-from-class="transform opacity-0 scale-95"
                                enter-to-class="transform opacity-100 scale-100"
                                leave-active-class="transition ease-in duration-75"
                                leave-from-class="transform opacity-100 scale-100"
                                leave-to-class="transform opacity-0 scale-95">
                        <MenuItems class="absolute left-0 z-10 mt-2 w-48 origin-top-right rounded-lg divide-y divide-gray-100 bg-white py-2 shadow-lg ring-1 ring-black/5 focus:outline-none">
                            <div>
                                <MenuItem v-for="language in render.languages"
                                          v-slot="{ active }"
                                          :key="language.id">
                                    <button :class="[active ? 'bg-gray-100' : '', currentLanguage.code === language.code ? 'font-semibold text-black' : 'text-gray-700', 'flex items-center w-full px-4 py-2 text-sm']"
                                            @click="selectLanguage(language)">
                                        <check-icon v-if="currentLanguage.code === language.code"
                                                    class="h-3 w-3 mr-2" />
                                        {{ language.name }}
                                    </button>
                                </MenuItem>
                            </div>
                        </MenuItems>
                    </transition>
                </HeadlessMenu>
            </div>
            <div class="flex items-center space-x-4">
                <HeadlessMenu as="div" class="relative">
                    <div>
                        <MenuButton class="reader-link flex items-center">
                            <share-icon class="h-8 w-8" />
                        </MenuButton>
                    </div>
                    <transition enter-active-class="transition ease-out duration-100"
                                enter-from-class="transform opacity-0 scale-95"
                                enter-to-class="transform opacity-100 scale-100"
                                leave-active-class="transition ease-in duration-75"
                                leave-from-class="transform opacity-100 scale-100"
                                leave-to-class="transform opacity-0 scale-95">
                        <MenuItems class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-lg divide-y divide-gray-100 bg-white py-2 shadow-lg ring-1 ring-black/5 focus:outline-none">
                            <div>
                                <MenuItem v-slot="{ active }">
                                    <a :class="[active ? 'bg-gray-100' : '', 'flex items-center w-full px-4 py-2 text-sm text-gray-700']"
                                       href="https://www.facebook.com/groups/207861980435264/?source_id=1509842252669349"
                                       target="_blank">
                                        <svg class="h-5 w-5 mr-2"
                                             xmlns="http://www.w3.org/2000/svg"
                                             fill="currentColor"
                                             viewBox="0 0 16 16">
                                            <path d="M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951z" />
                                        </svg>
                                        Facebook
                                    </a>
                                </MenuItem>
                                <MenuItem v-slot="{ active }">
                                    <a :class="[active ? 'bg-gray-100' : '', 'flex items-center w-full px-4 py-2 text-sm text-gray-700']"
                                       href="https://www.youtube.com/channel/UCO0qiBKrOpV89AIQ-jXZ4gw/featured"
                                       target="_blank">
                                        <svg class="h-5 w-5 mr-2"
                                             xmlns="http://www.w3.org/2000/svg"
                                             fill="currentColor"
                                             viewBox="0 0 16 16">
                                            <path d="M8.051 1.999h.089c.822.003 4.987.033 6.11.335a2.01 2.01 0 0 1 1.415 1.42c.101.38.172.883.22 1.402l.01.104.022.26.008.104c.065.914.073 1.77.074 1.957v.075c-.001.194-.01 1.108-.082 2.06l-.008.105-.009.104c-.05.572-.124 1.14-.235 1.558a2.007 2.007 0 0 1-1.415 1.42c-1.16.312-5.569.334-6.18.335h-.142c-.309 0-1.587-.006-2.927-.052l-.17-.006-.087-.004-.171-.007-.171-.007c-1.11-.049-2.167-.128-2.654-.26a2.007 2.007 0 0 1-1.415-1.419c-.111-.417-.185-.986-.235-1.558L.09 9.82l-.008-.104A31.4 31.4 0 0 1 0 7.68v-.123c.002-.215.01-.958.064-1.778l.007-.103.003-.052.008-.104.022-.26.01-.104c.048-.519.119-1.023.22-1.402a2.007 2.007 0 0 1 1.415-1.42c.487-.13 1.544-.21 2.654-.26l.17-.007.172-.006.086-.003.171-.007A99.788 99.788 0 0 1 7.858 2h.193zM6.4 5.209v4.818l4.157-2.408L6.4 5.209z" />
                                        </svg>
                                        YouTube
                                    </a>
                                </MenuItem>
                                <MenuItem v-slot="{ active }">
                                    <a :class="[active ? 'bg-gray-100' : '', 'flex items-center w-full px-4 py-2 text-sm text-gray-700']"
                                       href="https://www.instagram.com/thelegionforever/"
                                       target="_blank">
                                        <svg class="h-5 w-5 mr-2"
                                             xmlns="http://www.w3.org/2000/svg"
                                             fill="currentColor"
                                             viewBox="0 0 16 16">
                                            <path d="M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.917 3.917 0 0 0-1.417.923A3.927 3.927 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.916 3.916 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.926 3.926 0 0 0-.923-1.417A3.911 3.911 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0h.003zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599.28.28.453.546.598.92.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.47 2.47 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.478 2.478 0 0 1-.92-.598 2.48 2.48 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233 0-2.136.008-2.388.046-3.231.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92.28-.28.546-.453.92-.598.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045v.002zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92zm-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217zm0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334z" />
                                        </svg>
                                        Instagram
                                    </a>
                                </MenuItem>
                            </div>
                        </MenuItems>
                    </transition>
                </HeadlessMenu>
                <button v-if="isAutoPlaying"
                        type="button"
                        class="reader-link"
                        @click="stopAutoPlay()">
                    <play-circle-icon class="h-8 w-8 text-blue-500" />
                </button>
                <button v-else
                        type="button"
                        class="reader-link"
                        @click="autoPlay()">
                    <play-circle-icon class="h-8 w-8" />
                </button>
                <button type="button"
                        class="reader-link"
                        @click="normal()">
                    <square2-stack-icon class="h-8 w-8 rotate-180" />
                </button>
                <button type="button"
                        class="reader-link"
                        :class="{'opacity-50 pointer-events-none': !canZoomIn}"
                        @click="zoomIn()">
                    <magnifying-glass-plus-icon class="h-8 w-8" />
                </button>
                <button type="button"
                        class="reader-link"
                        :class="{'opacity-50 pointer-events-none': !canZoomOut}"
                        @click="zoomOut()">
                    <magnifying-glass-minus-icon class="h-8 w-8" />
                </button>
                <HeadlessMenu v-if="render.tracks.length"
                              as="div"
                              class="relative">
                    <div>
                        <MenuButton class="reader-link flex items-center">
                            <speaker-wave-icon v-if="isAudioEnabled" class="h-8 w-8" />
                            <speaker-x-mark-icon v-else class="h-8 w-8" />
                        </MenuButton>
                    </div>
                    <transition enter-active-class="transition ease-out duration-100"
                                enter-from-class="transform opacity-0 scale-95"
                                enter-to-class="transform opacity-100 scale-100"
                                leave-active-class="transition ease-in duration-75"
                                leave-from-class="transform opacity-100 scale-100"
                                leave-to-class="transform opacity-0 scale-95">
                        <MenuItems class="absolute right-0 z-10 mt-2 w-64 origin-top-right rounded-lg divide-y divide-gray-100 bg-white py-2 shadow-lg ring-1 ring-black/5 focus:outline-none">
                            <div>
                                <div class="px-4 py-2 flex items-center">
                                    <button v-if="isAudioEnabled"
                                            class="flex items-center text-gray-700 mr-2"
                                            @click="stopAudio()">
                                        <pause-icon class="h-5 w-5" />
                                    </button>
                                    <button v-else
                                            class="flex items-center text-gray-700 mr-2"
                                            @click="playAudio()">
                                        <play-icon class="h-5 w-5" />
                                    </button>
                                    <div class="progress h-2 flex-1 bg-gray-300 rounded-full"
                                         :class="{'pointer-events-none': !isAudioEnabled}"
                                         @click.stop="setVolume($event)">
                                        <div class="h-2 rounded-full"
                                             :class="{'bg-gray-600': !isAudioEnabled, 'bg-blue-500': isAudioEnabled}"
                                             :style="{width: volumePosition()}"></div>
                                    </div>
                                </div>
                                <MenuItem v-for="track in render.tracks"
                                          v-slot="{ active }"
                                          :key="track.id">
                                    <button :class="[active ? 'bg-gray-100' : '', currentTrack.id === track.id ? 'font-semibold text-black' : 'text-gray-700', 'flex items-center w-full px-4 py-2 text-sm']"
                                            @click="selectTrack(track)">
                                        <check-icon v-if="currentTrack.id === track.id"
                                                    class="h-3 w-3 mr-2" />
                                        {{ track.name }}
                                    </button>
                                </MenuItem>
                            </div>
                        </MenuItems>
                    </transition>
                </HeadlessMenu>
                <button type="button"
                        class="reader-link"
                        @click="fullscreen()">
                    <arrows-pointing-out-icon class="h-8 w-8"
                                              :class="{'text-blue-500': isFullscreen}" />
                </button>
            </div>
        </nav>
        <audio v-if="currentTrack"
               class="hidden"
               loop>
            <source :src="currentTrack.path" type="audio/mpeg">
        </audio>
        <div class="reader-container">
            <Splide ref="main"
                    :has-track="false"
                    :options="mainOptions">
                <div class="splide__arrows">
                    <button class="splide__arrow--prev fixed top-1/2 left-4 -translate-y-1/2 transition-transform text-white z-30"
                            :class="{
                                '-translate-x-20': !isLeftArrowVisible
                            }">
                        <chevron-right-icon class="h-16 w-16" />
                    </button>
                    <button class="splide__arrow--next fixed top-1/2 right-4 -translate-y-1/2 transition-transform text-white z-30"
                            :class="{
                                'translate-x-20': !isRightArrowVisible
                            }">
                        <chevron-right-icon class="h-16 w-16" />
                    </button>
                </div>
                <SplideTrack>
                    <SplideSlide v-for="picture in pictures" :key="picture.id">
                        <img :src="picture.src" alt="">
                    </SplideSlide>
                </SplideTrack>
                <div class="splide__progress">
                    <div class="splide__progress__bar"></div>
                </div>
            </Splide>
        </div>
        <div class="reader-thumbnails"
             :class="{'translate-y-full': !isThumbVisible}">
            <Splide ref="thumb"
                    :options="thumbOptions">
                <SplideSlide v-for="(picture, index) in pictures" :key="picture.id">
                    <span class="flex justify-center text-sm text-white font-bold">
                        {{ index + 1 }}
                    </span>
                    <img :src="picture.thumb" alt="">
                </SplideSlide>
            </Splide>
        </div>
    </div>
    <!-- eslint-enable max-len -->
</template>

<script setup>
import {
    ArrowsPointingOutIcon,
    ChatBubbleBottomCenterTextIcon,
    CheckIcon,
    ChevronRightIcon,
    PauseIcon,
    PlayIcon,
    PlayCircleIcon,
    ShareIcon,
    SpeakerXMarkIcon,
    SpeakerWaveIcon,
    Square2StackIcon,
    MagnifyingGlassPlusIcon,
    MagnifyingGlassMinusIcon
} from '@heroicons/vue/24/outline';

import {
    Menu as HeadlessMenu,
    MenuButton,
    MenuItem,
    MenuItems
} from '@headlessui/vue';

import { router } from '@inertiajs/vue3';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide';
import Screenfull from 'screenfull';

import {
    ref,
    computed,
    watch,
    onMounted,
    onUnmounted,
    defineProps
} from 'vue';

import useMetric from '../../use/useMetric';

const {
    app,
    start,
    comic,
    render,
    defaultLanguage
} = defineProps({
    app: {
        type: Object,
        required: true
    },

    start: {
        type: Number,
        required: true
    },

    comic: {
        type: Object,
        required: true
    },

    render: {
        type: Object,
        required: true
    },

    defaultLanguage: {
        type: String,
        default: ''
    }
});

const { startMetric, sendMetric, addMetric } = useMetric();
const isAutoPlaying = ref(false);
const isAudioEnabled = ref(false);
const isAudioPlaying = ref(false);
const isFullscreen = ref(false);
const isNavVisible = ref(true);
const isLeftArrowVisible = ref(true);
const isRightArrowVisible = ref(true);
const isThumbVisible = ref(true);
const currentLanguage = ref();
const currentTrack = ref();
const navTimeout = ref(undefined);
const leftArrowTimeout = ref(undefined);
const rightArrowTimeout = ref(undefined);
const thumbTimeout = ref(undefined);
const scale = ref(1);
const isDragging = ref(false);
const audioElement = ref(undefined);
const volume = ref(0.5);
const el = ref();

const controlsTimer = 3000;
const maxScale = 1.6;
const maxWidth = 1920;
const maxHeight = 1080;

const dragInit = ref({
    x: 0,
    y: 0
});

if (render.languages.length) {
    if (defaultLanguage) {
        currentLanguage.value = _.find(
            render.languages, current => defaultLanguage === current.code
        );
    } else {
        const [first] = render.languages;

        currentLanguage.value = first;
    }
}

const pictures = computed(() => _.filter(
    render.pictures,
    picture => picture.language_id === currentLanguage.value.id
));

const getStartX = () => (window.innerWidth - maxWidth * scale.value) / 2;
const getStartY = () => (window.innerHeight - maxHeight * scale.value) / 2;

const dragOffset = ref({
    x: getStartX(),
    y: getStartY()
});

if (render.tracks.length) {
    const [first] = render.tracks;

    currentTrack.value = first;
}

const canZoomIn = computed(() => scale.value < maxScale);
const canZoomOut = computed(() => scale.value > 1);

const main = ref();
const mainOptions = {
    type: 'fade',
    pagination: false,
    arrows: true,
    cover: true,
    rewind: true,
    height: '100vh',
    pauseOnHover: false,
    pauseOnFocus: false,
    start
};

const thumb = ref();
const thumbOptions = {
    fixedWidth: 104,
    fixedHeight: 78,
    isNavigation: true,
    gap: 10,
    focus: 'center',
    pagination: false,
    arrows: false,
    cover: true,
    start,
    dragMinThreshold: {
        mouse: 4,
        touch: 10
    },
    breakpoints: {
        640: {
            fixedWidth: 66,
            fixedHeight: 58
        }
    }
};

const initAudio = () => {
    audioElement.value = el.value.querySelector('audio');
    audioElement.value.volume = volume.value;

    audioElement.value.addEventListener('play', () => {
        isAudioPlaying.value = true;
    }, false);

    audioElement.value.addEventListener('pause', () => {
        isAudioPlaying.value = false;
    }, false);
};

const startNavTimer = () => {
    navTimeout.value = setTimeout(() => {
        isNavVisible.value = false;
    }, controlsTimer);
};

const resetNavTimer = () => {
    isNavVisible.value = true;
    clearTimeout(navTimeout.value);
    startNavTimer();
};

const startLeftArrowTimer = () => {
    leftArrowTimeout.value = setTimeout(() => {
        isLeftArrowVisible.value = false;
    }, controlsTimer);
};

const resetLeftArrowTimer = () => {
    isLeftArrowVisible.value = true;
    clearTimeout(leftArrowTimeout.value);
    startLeftArrowTimer();
};

const startRightArrowTimer = () => {
    rightArrowTimeout.value = setTimeout(() => {
        isRightArrowVisible.value = false;
    }, controlsTimer);
};

const resetRightArrowTimer = () => {
    isRightArrowVisible.value = true;
    clearTimeout(rightArrowTimeout.value);
    startRightArrowTimer();
};

const startThumbTimer = () => {
    thumbTimeout.value = setTimeout(() => {
        isThumbVisible.value = false;
    }, controlsTimer);
};

const resetThumbTimer = () => {
    isThumbVisible.value = true;
    clearTimeout(thumbTimeout.value);
    startThumbTimer();
};

const resetControlsTimer = () => {
    resetNavTimer();
    resetLeftArrowTimer();
    resetRightArrowTimer();
    resetThumbTimer();
};

const handleMouseAreas = e => {
    const xPercent = e.clientX / window.innerWidth;
    const yPercent = e.clientY / window.innerHeight;

    if (xPercent < 0.2) {
        resetLeftArrowTimer();
    }

    if (xPercent > 0.8) {
        resetRightArrowTimer();
    }

    if (yPercent < 0.2) {
        resetNavTimer();
    }

    if (yPercent > 0.8) {
        resetThumbTimer();
    }
};

const setupControls = () => {
    document.addEventListener('mousemove', handleMouseAreas, false);
    document.addEventListener('mousedown', handleMouseAreas, false);
    document.addEventListener('keypress', resetControlsTimer, false);

    startNavTimer();
    startLeftArrowTimer();
    startRightArrowTimer();
    startThumbTimer();
};

const selectLanguage = language => {
    currentLanguage.value = language;

    if (main.value && main.value.splide) {
        const { index } = main.value.splide;

        router.get(route('reader_classic', [app.locale, comic.id, currentLanguage.value.code]), _.pickBy({
            start: index
        }), {
            preserveState: false
        });
    }
};

const volumePosition = () => `${volume.value * 100}%`;

const setVolume = e => {
    const bcr = el.value.querySelector('.progress').getBoundingClientRect();

    volume.value = Math.min(Math.max(0, (e.clientX - bcr.left) / bcr.width), 1);
};

const selectTrack = track => {
    currentTrack.value = track;

    if (isAudioPlaying.value) {
        audioElement.value.load();
        audioElement.value.play();
    } else {
        audioElement.value.load();
    }
};

const autoPlay = () => {
    isAutoPlaying.value = true;

    addMetric('autoplay_on');
};

const stopAutoPlay = () => {
    isAutoPlaying.value = false;

    addMetric('autoplay_off');
};

const handleAutoPlay = () => {
    if (main.value && main.value.splide) {
        const { Autoplay } = main.value.splide.Components;

        if (isAutoPlaying.value) {
            Autoplay.play();
        } else {
            Autoplay.pause();
        }
    }
};

const normal = () => {
    scale.value = 1;
};

const zoomIn = () => {
    scale.value += 0.2;
};

const zoomOut = () => {
    scale.value -= 0.2;
};

const playAudio = () => {
    isAudioEnabled.value = true;
    audioElement.value.play();

    addMetric('audio_on');
};

const stopAudio = () => {
    isAudioEnabled.value = false;
    audioElement.value.pause();

    addMetric('audio_off');
};

const fullscreen = () => {
    if (Screenfull.isEnabled) {
        Screenfull.toggle();
    }
};

const getXY = e => ({
    x: (e.clientX || e.touches[0].clientX) - window.innerWidth,
    y: (e.clientY || e.touches[0].clientY) - window.innerHeight
});

const dragStart = e => {
    isDragging.value = true;

    const { x, y } = getXY(e);

    dragInit.value.x = x - dragOffset.value.x;
    dragInit.value.y = y - dragOffset.value.y;
};

const dragStop = () => {
    isDragging.value = false;
};

const dragMove = e => {
    if (!isDragging.value) {
        return;
    }

    if (main.value && scale.value > 1) {
        const slides = main.value.$el.querySelectorAll('.splide__slide');
        const { x, y } = getXY(e);

        dragOffset.value.x = x - dragInit.value.x;
        dragOffset.value.y = y - dragInit.value.y;

        slides.forEach(slide => {
            slide.style.setProperty('background-position', `${dragOffset.value.x}px ${dragOffset.value.y}px`, 'important');
        });
    }
};

watch(scale, () => {
    if (main.value && main.value.splide) {
        const slides = main.value.$el.querySelectorAll('.splide__slide');
        const { Drag } = main.value.splide.Components;

        slides.forEach(slide => {
            if (!slide.dataset.transform) {
                slide.dataset.transform = slide.style.transform;
            }

            if (!slide.dataset.backgroundPosition) {
                slide.dataset.backgroundPosition = slide.style.backgroundPosition;
            }

            if (scale.value === 1) {
                Drag.disable(false);

                slide.classList.remove('is-zooming');
                slide.style.setProperty('background-position', slide.dataset.backgroundPosition);

                dragOffset.value.x = getStartX();
                dragOffset.value.y = getStartY();
            } else {
                Drag.disable(true);

                slide.classList.add('is-zooming');
                slide.style.setProperty('background-position', `${dragOffset.value.x}px ${dragOffset.value.y}px`);
            }

            slide.style.transform = `${slide.dataset.transform} scale(${scale.value})`;
        });
    }
});

watch(isAutoPlaying, handleAutoPlay);

watch(isFullscreen, () => {
    addMetric(`fullscreen_${isFullscreen.value
        ? 'on'
        : 'off'}`);
});

watch(volume, () => {
    audioElement.value.volume = volume.value;
});

onMounted(() => {
    startMetric(render.id, app.locale);
    addMetric('play');
    addMetric('view');

    if (currentTrack.value) {
        initAudio();
    }

    setupControls();

    Screenfull.on('change', () => {
        isFullscreen.value = Screenfull.isFullscreen;
    });

    if (thumb.value && thumb.value.splide) {
        thumb.value.splide.on('move', normal);

        if (main.value && main.value.splide) {
            main.value.splide.on('move', newIndex => {
                sendMetric(app.locale, newIndex);
                addMetric('view');
            });

            main.value.splide.sync(thumb.value.splide);
        }
    }

    document.addEventListener('mousedown', dragStart, false);
    document.addEventListener('mouseup', dragStop, false);
    document.addEventListener('mousemove', dragMove, false);

    document.addEventListener('touchstart', dragStart, false);
    document.addEventListener('touchend', dragStop, false);
    document.addEventListener('touchmove', dragMove, false);
});

onUnmounted(() => {
    document.removeEventListener('mousemove', handleMouseAreas, false);
    document.removeEventListener('mousedown', handleMouseAreas, false);
    document.removeEventListener('keypress', resetControlsTimer, false);

    document.removeEventListener('mousedown', dragStart, false);
    document.removeEventListener('mouseup', dragStop, false);
    document.removeEventListener('mousemove', dragMove, false);

    document.removeEventListener('touchstart', dragStart, false);
    document.removeEventListener('touchend', dragStop, false);
    document.removeEventListener('touchmove', dragMove, false);
});
</script>
